import { currentUserQuery } from "@common/api/users"
import {
  partInstanceUtilizationQuery,
  useDeletePartInstanceReadingMutation,
} from "@common/api/utilization"
import { ConfirmDialog } from "@common/components/shared/confirm-dialog"
import { formatDate } from "@common/lib/date-utils"
import { formatDecimal } from "@common/lib/number"
import { hasPermission } from "@common/lib/permissions"
import { handleError } from "@common/utils"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import useCustomToast from "@/hooks/use-custom-toast"
import { UNIT_ABBREVIATIONS } from "@/routes/_layout/maintenance-programmes_/$programmeId/-lib/schedule"
import { eventLabel } from "../../../../-lib/utilization"

export const Route = createFileRoute(
  "/_layout/aircraft_/$aircraftId/part-instances/$partInstanceId/readings/$readingId/delete",
)({
  beforeLoad: async ({ context: { queryClient }, params, search }) => {
    const me = await queryClient.ensureQueryData(currentUserQuery())
    if (!hasPermission(me, "utilization:delete")) {
      throw redirect({ to: "/aircraft/$aircraftId", params, search })
    }
  },
  loader: ({ context: { queryClient }, params: { partInstanceId } }) =>
    queryClient.ensureQueryData(partInstanceUtilizationQuery(partInstanceId)),
  component: DeleteReadingDialog,
})

function DeleteReadingDialog(): React.ReactElement {
  const { aircraftId, partInstanceId, readingId } = Route.useParams()
  const { data } = useSuspenseQuery(
    partInstanceUtilizationQuery(partInstanceId),
  )
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const deleteMutation = useDeletePartInstanceReadingMutation()
  const reading = data.data.find((candidate) => candidate.id === readingId)
  const description = reading
    ? [
        `${eventLabel(reading.event)} reading of`,
        `${formatDecimal(reading.value, 2)} ${UNIT_ABBREVIATIONS[reading.unit]}`,
        `as of ${formatDate(reading.as_of)} will be archived.`,
      ].join(" ")
    : "This reading will be archived."

  const close = () =>
    navigate({
      to: "/aircraft/$aircraftId",
      params: { aircraftId },
      search: true,
    })

  const confirm = () =>
    deleteMutation.mutate(
      { part_instance_id: partInstanceId, utilization_id: readingId },
      {
        onSuccess: () => {
          showToast("Archived", "Reading archived.", "success")
          close()
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  return (
    <ConfirmDialog
      title="Archive this reading?"
      description={description}
      action="Archive"
      tone="default"
      pendingAction="Archiving…"
      pending={deleteMutation.isPending}
      onConfirm={confirm}
      onCancel={close}
    />
  )
}
